'use client';

import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createChart,
  HistogramSeries,
  ColorType,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import { formatUSD, formatUSDCompact, formatTokenCompact, cn, formatTimeAgo } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { CoinImage } from '@/components/shared';
import type { V2GridRewardEvent } from '@/lib/types/v2';

type Timeframe = '24h' | '7d' | '1m' | 'all';
const TIMEFRAME_OPTIONS: { value: Timeframe; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7D' },
  { value: '1m', label: '1M' },
  { value: 'all', label: 'ALL' },
];

function getTimeframeSince(tf: Timeframe): number {
  const now = Math.floor(Date.now() / 1000);
  switch (tf) {
    case '24h': return now - 86400;
    case '7d': return now - 7 * 86400;
    case '1m': return now - 30 * 86400;
    case 'all': return 0;
  }
}

export type { Timeframe as RewardsTimeframe };

interface RewardsTabProps {
  rewardEvents: V2GridRewardEvent[];
  gridId: string;
  timeframe?: Timeframe;
  onTimeframeChange?: (tf: Timeframe) => void;
  isUsdMode?: boolean;
  coinImageUrl?: string | null;
  /** Pending adapter yield in the same display-scaled format as rewardEvent amounts (value * 1e18) */
  pendingYield?: number;
  /** Pre-bucketed chart data from the server (preferred over client-side bucketing) */
  chartBuckets?: { time: number; value: number }[];
  /** Whether chart data is loading from the server */
  chartLoading?: boolean;
}

export function RewardsTab({ rewardEvents, gridId, timeframe: externalTimeframe, onTimeframeChange, isUsdMode = true, coinImageUrl, pendingYield = 0, chartBuckets, chartLoading }: RewardsTabProps) {
  const [internalTimeframe, setInternalTimeframe] = useState<Timeframe>('all');
  const feedTimeframe = externalTimeframe ?? internalTimeframe;
  const setFeedTimeframe = onTimeframeChange ?? setInternalTimeframe;
  const pendingDisplay = pendingYield / 1e18;
  /** Server buckets not ready yet — show skeleton instead of an empty chart or token fallback. */
  const showRewardsSkeleton = !!chartLoading && (!chartBuckets || chartBuckets.length === 0);

  const chartData = useMemo(() => {
    if (chartBuckets && chartBuckets.length > 0) {
      return chartBuckets.map(b => ({ time: b.time as unknown as Time, value: b.value }));
    }

    const now = Math.floor(Date.now() / 1000);
    const BUCKET_CONFIG: Record<Timeframe, { since: number; bucket: number }> = {
      '24h': { since: now - 86400,      bucket: 5 * 60 },
      '7d':  { since: now - 7 * 86400,  bucket: 3600 },
      '1m':  { since: now - 30 * 86400, bucket: 4 * 3600 },
      'all': { since: 0,                bucket: 86400 },
    };
    const config = BUCKET_CONFIG[feedTimeframe];
    const since = config.since || Math.min(...rewardEvents.map(e => e.timestamp), now);

    const bucketMap = new Map<number, number>();
    for (const ev of rewardEvents) {
      if (ev.timestamp < since) continue;
      const key = Math.floor(ev.timestamp / config.bucket) * config.bucket;
      bucketMap.set(key, (bucketMap.get(key) ?? 0) + Number(ev.amount) / 1e18);
    }

    if (pendingDisplay > 0) {
      const key = Math.floor(now / config.bucket) * config.bucket;
      bucketMap.set(key, (bucketMap.get(key) ?? 0) + pendingDisplay);
    }

    return [...bucketMap.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([t, v]) => ({ time: t as unknown as Time, value: v }));
  }, [chartBuckets, rewardEvents, feedTimeframe, pendingDisplay]);

  const total24h = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const since = now - 86400;
    return rewardEvents
      .filter(e => e.timestamp >= since)
      .reduce((sum, e) => sum + Number(e.amount) / 1e18, 0);
  }, [rewardEvents]);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleCrosshairMove = useCallback((param: any) => {
    const tooltip = tooltipRef.current;
    const container = containerRef.current;
    if (!tooltip || !container) return;

    if (!param.point || !param.time || param.point.x < 0 || param.point.y < 0) {
      tooltip.style.display = 'none';
      return;
    }

    const ts = param.time as number;
    const series = seriesRef.current;
    const data = series ? param.seriesData.get(series) : null;
    const val = data?.value as number | undefined;

    if (val == null) { tooltip.style.display = 'none'; return; }

    const d = new Date(ts * 1000);
    const timeStr = d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });

    tooltip.innerHTML = `
      <div class="text-[10px] text-muted-foreground font-mono">${timeStr}</div>
      <div class="flex items-center justify-between gap-4">
        <span class="flex items-center gap-1.5">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
          <span class="text-muted-foreground text-[11px]">Rewards</span>
        </span>
        <span class="font-mono text-emerald-400 text-[11px] inline-flex items-center gap-1">${isUsdMode ? formatUSD(val) : `<img src="${coinImageUrl || '/lobster.png'}" alt="" class="inline-block w-3.5 h-3.5 rounded-full" style="${!coinImageUrl ? 'filter:grayscale(1) brightness(0.4)' : ''}" onerror="this.src='/lobster.png';this.style.filter='grayscale(1) brightness(0.4)'" />${formatTokenCompact(val)}`}</span>
      </div>`;
    tooltip.style.display = 'block';

    const rect = container.getBoundingClientRect();
    const px = rect.left + param.point.x;
    const py = rect.top + param.point.y;
    const tw = tooltip.clientWidth;
    const th = tooltip.clientHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = px + 16 + tw > vw ? px - tw - 12 : px + 16;
    const top = py - 20 + th > vh ? py - th - 8 : Math.max(4, py - 20);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }, [isUsdMode, coinImageUrl]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || showRewardsSkeleton) return;

    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: '#888', attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: 'rgba(255,255,255,0.03)' } },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: 'rgba(255,255,255,0.15)', width: 1, style: 3, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      rightPriceScale: { visible: false },
      timeScale: { visible: false, borderVisible: false },
      handleScroll: false,
      handleScale: false,
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const series = chart.addSeries(HistogramSeries, {
      color: 'rgba(52,211,153,0.7)',
      priceFormat: { type: 'price', minMove: 0.0001 },
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      chart.resize(container.clientWidth, container.clientHeight);
    });
    ro.observe(container);

    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [handleCrosshairMove, showRewardsSkeleton]);

  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || showRewardsSkeleton) return;

    // Always push the new data — including an empty array — so switching
    // to a timeframe with no rewards (e.g. 1M → 7D on a board that's been
    // idle for a week) clears the previous histogram bars instead of
    // leaving them painted under the new tab. `fitContent()` is meaningless
    // on an empty series and would throw on some lightweight-charts builds,
    // so we gate it on `chartData.length > 0`.
    series.setData(chartData as any);
    if (chartData.length > 0) chart.timeScale().fitContent();
  }, [chartData, showRewardsSkeleton]);

  const filteredRewards = useMemo(() => {
    const since = getTimeframeSince(feedTimeframe);
    return [...rewardEvents]
      .filter(e => e.timestamp >= since && Number(e.amount) / 1e18 >= 0.0001)
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [rewardEvents, feedTimeframe]);

  const feedTotal = useMemo(() =>
    filteredRewards.reduce((sum, e) => sum + Number(e.amount) / 1e18, 0),
    [filteredRewards]
  );

  const chartTotal = useMemo(() =>
    chartBuckets?.reduce((s, b) => s + b.value, 0) ?? 0,
    [chartBuckets]
  );

  const displayTotal = chartTotal > 0 ? chartTotal : feedTotal;

  const [rewardDisplayCount, setRewardDisplayCount] = useState(20);
  const displayRewards = filteredRewards.slice(0, rewardDisplayCount);
  const hasMoreRewards = rewardDisplayCount < filteredRewards.length;

  const formatRewardAmount = (raw: string) => {
    const val = Number(raw) / 1e18;
    if (isUsdMode) return formatUSDCompact(val);
    return val > 0 ? formatTokenCompact(val) : '0';
  };

  const getRewardColor = useCallback((raw: string) => {
    if (!isUsdMode) return { dot: 'bg-emerald-400', text: 'text-emerald-400' };
    const val = Number(raw) / 1e18;
    if (val > 100) return { dot: 'bg-amber-400', text: 'text-amber-400' };
    if (val > 50) return { dot: 'bg-purple-400', text: 'text-purple-400' };
    if (val > 10) return { dot: 'bg-accent-blue-light', text: 'text-accent-blue-light' };
    return { dot: 'bg-emerald-400', text: 'text-emerald-400' };
  }, [isUsdMode]);

  const tokenImg = !isUsdMode ? <span className="inline-block w-4 h-4 rounded-full overflow-hidden shrink-0"><CoinImage src={coinImageUrl} alt="" /></span> : null;

  const headerLabel = (() => {
    const base = chartTotal > 0 ? chartTotal : feedTotal > 0 ? feedTotal : total24h > 0 ? total24h : 0;
    const val = base + pendingDisplay;
    if (isUsdMode) return formatUSDCompact(val);
    return formatTokenCompact(val);
  })();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-end gap-1.5 min-w-0">
          {tokenImg}
          <span className="text-lg font-bold font-mono leading-tight text-emerald-400 min-h-[1.5rem] flex items-center">
            {showRewardsSkeleton ? (
              <span className="inline-block h-7 w-24 rounded-[3px] bg-muted/60 animate-pulse" aria-hidden />
            ) : (
              headerLabel
            )}
          </span>
          <span className="text-[10px] text-muted-foreground leading-tight mb-px">rewards</span>
        </div>
        <div className="flex gap-0.5">
          {TIMEFRAME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => { setFeedTimeframe(opt.value); setRewardDisplayCount(20); }}
              className={cn(
                'text-[9px] px-1.5 py-0.5 rounded transition-colors',
                feedTimeframe === opt.value
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-44 -mx-1">
        {showRewardsSkeleton ? (
          <div
            className="w-full h-full rounded-[4px] bg-muted/30 animate-pulse border border-border/20"
            aria-busy
            aria-label="Loading rewards chart"
          />
        ) : (
          <>
            <div ref={containerRef} className="w-full h-full" />
            {/*
              Portal the tooltip to <body> so its `position: fixed`
              measures against the viewport. Inside the lobby's
              `DraggableFloatingPanel`, the parent `motion.div`
              always carries a `transform`, which would otherwise
              turn `fixed` into "fixed-relative-to-the-panel" and
              the tooltip would land off-screen at the recorded
              `getBoundingClientRect()` coords. Same Tailwind
              classes, same positioning math — only the mount
              point changes. See `floor-price-chart.tsx` for the
              symmetric fix on the sales/listings charts.
            */}
            {typeof document !== 'undefined' && createPortal(
              <div
                ref={tooltipRef}
                className="fixed z-[9999] pointer-events-none rounded-lg border bg-card/95 backdrop-blur-sm p-2.5 shadow-lg text-sm space-y-1.5 min-w-[140px]"
                style={{ display: 'none', top: 0, left: 0 }}
              />,
              document.body,
            )}
          </>
        )}
      </div>

      <div className="border-t border-border/50 mt-3 pt-2 space-y-1">
        <p className="text-[10px] text-zinc-500 font-medium px-1 mb-1">
          Rewards{displayTotal > 0 ? ` · ${isUsdMode ? `$${displayTotal.toFixed(2)}` : formatTokenCompact(displayTotal)}` : ''}
          {pendingDisplay > 0 && (
            <span className="text-amber-400/80 ml-1">
              + {isUsdMode ? `$${pendingDisplay.toFixed(2)}` : formatTokenCompact(pendingDisplay)} in adapters
            </span>
          )}
        </p>
        <RewardsList
          rewards={displayRewards}
          hasMore={hasMoreRewards}
          onLoadMore={() => setRewardDisplayCount(c => c + 20)}
          formatAmount={formatRewardAmount}
          getColor={getRewardColor}
          tokenImg={tokenImg}
        />
      </div>
    </div>
  );
}

function RewardsList({ rewards, hasMore, onLoadMore, formatAmount, getColor, tokenImg }: {
  rewards: V2GridRewardEvent[];
  hasMore: boolean;
  onLoadMore: () => void;
  formatAmount: (raw: string) => string;
  getColor: (raw: string) => { dot: string; text: string };
  tokenImg: React.ReactNode;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onLoadMore(); },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (rewards.length === 0) {
    return (
      <div className="flex items-center justify-center py-3 text-[10px] font-mono text-zinc-500">
        No rewards yet
      </div>
    );
  }

  return (
    <div className="max-h-[148px] space-y-0 overflow-y-auto pr-0.5">
      {rewards.map(ev => {
        const rc = getColor(ev.amount);
        return (
          <div
            key={ev.id}
            className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-1 px-0.5 py-0.5 text-[10px] leading-tight text-zinc-300 hover:bg-white/[0.04] rounded-[2px] transition-colors"
          >
            <span className={cn('h-1 w-1 rounded-full shrink-0 justify-self-center', rc.dot)} />
            <span className={cn('min-w-0 font-mono text-[10px] tabular-nums flex items-center gap-1', rc.text)}>
              {tokenImg}
              {formatAmount(ev.amount)}
            </span>
            <span className="shrink-0 text-right font-mono text-[9px] text-zinc-500 whitespace-nowrap">
              {formatTimeAgo(ev.timestamp * 1000)}
            </span>
          </div>
        );
      })}
      {hasMore && <div ref={sentinelRef} className="h-1" />}
    </div>
  );
}
